import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { LoadingIndicator } from '@spotify-internal/encore-web';

import { fetchGlobalSettings, GlobalSettings } from 'lib/settings/settings';

interface SettingsContextValue {
  settings: GlobalSettings;
  loading: boolean;
  error: Error | null;
}

const SettingsContext = createContext<SettingsContextValue | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

export interface SettingsProps {
  maintenanceMode?: boolean;
}

/**
 * HOC to inject settings as props into a component
 * Mirrors the pattern used by withExperimentation
 */
export const withSettings: any = (Component: any) => (props: any) => {
  const { settings } = useSettings();
  return <Component {...props} maintenanceMode={settings.isMaintenanceModeEnabled} />;
};

const SettingsProvider = ({ children }: PropsWithChildren<{}>) => {
  const [settings, setSettings] = useState<GlobalSettings>({ isMaintenanceModeEnabled: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const fetchedSettings = await fetchGlobalSettings();
        setSettings(fetchedSettings);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load settings'));
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LoadingIndicator />
      </div>
    );
  }

  return <SettingsContext.Provider value={{ settings, loading, error }}>{children}</SettingsContext.Provider>;
};

export default SettingsProvider;
